/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../config";

// Column definitions
const MD_COLS = [
  'date','time','vehicle','owner','driver',
  'litres','amount','payType','paid','station',
  'openKm','closeKm','totalKm',
  'expKmL','actualKmL','expKm','actualKm','kmDiff'
];
const MD_UPPERCASE = ['vehicle','owner','driver','paid','station','payType'];

const DL_COLS = [
  { key:'date',       label:'Date' },
  { key:'time',       label:'Time' },
  { key:'vehicle',    label:'Vehicle No' },
  { key:'owner',      label:'Owner Name' },
  { key:'driver',     label:'Driver' },
  { key:'litres',     label:'Ltrs' },
  { key:'amount',     label:'Amount' },
  { key:'payType',    label:'Payment Type' },
  { key:'paid',       label:'Paid By' },
  { key:'station',    label:'Fuel Station' },
  { key:'openKm',     label:'Opening KM' },
  { key:'closeKm',    label:'Closing KM' },
  { key:'totalKm',    label:'Total KM' },
  { key:'expKmL',     label:'Exp KM/L' },
  { key:'actualKmL',  label:'Actual KM/L' },
  { key:'expKm',      label:'Exp KM' },
  { key:'actualKm',   label:'Actual KM' },
  { key:'kmDiff',     label:'KM Diff' },
];

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEARS  = ['2024','2025','2026','2027'];
const DAYS   = Array.from({length:31}, (_,i) => String(i+1).padStart(2,'0'));
const PAY_TYPES = ['Cash','PhonePe','UPI','Card','Credit','NEFT'];

const emptyRow = () => ({
  date:'', time:'', vehicle:'', owner:'', driver:'',
  litres:'', amount:'', payType:'', paid:'', station:'',
  openKm:'', closeKm:'', totalKm:'',
  expKmL:'', actualKmL:'', expKm:'', actualKm:'', kmDiff:''
});

const READONLY_COLS = new Set([12,13,14,15,16,17]);

function calcRow(row) {
  const litres  = parseFloat(row.litres)  || 0;
  const openKm  = parseFloat(row.openKm)  || 0;
  const closeKm = parseFloat(row.closeKm) || 0;
  const expKmL  = parseFloat(row.expKmL)  || 0;

  let totalKm = 0;
  if (openKm > 0 && closeKm > openKm) {
    totalKm = closeKm - openKm;
  } else {
    const fallback = parseFloat(row.totalKm) || parseFloat(row.runKm) || 0;
    totalKm = fallback > 0 ? fallback : 0;
  }

  const actualKmL = (litres > 0 && totalKm > 0) ? +(totalKm / litres).toFixed(1) : '';
  const expKm     = (litres > 0 && expKmL > 0)  ? +(litres * expKmL).toFixed(1)  : '';
  const actualKm  = totalKm > 0 ? totalKm : '';
  const diff      = (actualKm !== '' && expKm !== '') ? +(actualKm - expKm).toFixed(1) : '';

  return {
    ...row,
    totalKm:   totalKm  || '',
    actualKmL: actualKmL,
    expKm:     expKm,
    actualKm:  actualKm,
    kmDiff:    diff,
  };
}

/** Normalize KM fields from API (Admin Dashboard uses openKm, closeKm, runKm). */
function kmFromFill(f) {
  const str = (v) =>
    v != null && v !== "" && !Number.isNaN(Number(v)) ? String(v) : "";
  return {
    openKm: str(f.openKm),
    closeKm: str(f.closeKm),
    totalKm: str(f.runKm != null && f.runKm !== "" ? f.runKm : f.totalKm),
  };
}

const COL_HEADERS = [
  { label:'Date',            sortable:true  },
  { label:'Time',            sortable:false },
  { label:'Vehicle No',      sortable:false },
  { label:'Owner Name',      sortable:false },
  { label:'Driver',          sortable:false },
  { label:'Ltrs',            sortable:false },
  { label:'Amount',          sortable:false },
  { label:'Payment Type',    sortable:false },
  { label:'Paid By',         sortable:false },
  { label:'Fuel Station',    sortable:false },
  { label:'Opening KM',      sortable:false },
  { label:'Closing KM',      sortable:false },
  { label:'Total KM',        sortable:false },
  { label:'Exp KM/L',        sortable:false },
  { label:'Actual KM/L',     sortable:false },
  { label:'Exp KM',          sortable:false },
  { label:'Actual KM',       sortable:false },
  { label:'KM Difference',   sortable:false },
];

// Column group backgrounds (alternating light tints for readability)
const COL_BG = [
  '','','','','',
  '#f9fafb','#f9fafb',
  '','','',
  '#f9fafb','#f9fafb','#f9fafb',
  '#fffbf0','#fffbf0',
  '#fffbf0','#fffbf0',
  '#fffbf0',
];

export default function MonthlyDataEntry() {
  const now = new Date();
  const [selMonth,  setSelMonth]  = useState(MONTHS[now.getMonth()]);
  const [selYear,   setSelYear]   = useState(String(now.getFullYear()));
  const [rows,      setRows]      = useState(() => Array.from({length:20}, emptyRow));
  const [history,   setHistory]   = useState([]);
  const [future,    setFuture]    = useState([]);
  const [sel,       setSel]       = useState({ r1:-1,c1:-1,r2:-1,c2:-1 });
  const [dragging,  setDragging]  = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');
  const [sortCol,   setSortCol]   = useState(-1);
  const [sortAsc,   setSortAsc]   = useState(true);

  const [dlMonth,   setDlMonth]   = useState('');
  const [dlYear,    setDlYear]    = useState('');
  const [dlDay,     setDlDay]     = useState('');
  const [dlVehicle, setDlVehicle] = useState('');
  const [dlCols,    setDlCols]    = useState(new Set(DL_COLS.map(c => c.key)));
  const [dlCount,   setDlCount]   = useState(0);

  const tableRef = useRef(null);

  useEffect(() => {
    loadFromBackend();
  }, [selMonth, selYear]);

  useEffect(() => {
    setDlCount(getDlRows().length);
  }, [rows, dlMonth, dlYear, dlDay, dlVehicle]);

  const loadFromBackend = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/fills/${selMonth}-${selYear}`);
      const mData = res.data?.data || {};
      const loaded = [];
      Object.entries(mData).forEach(([vehicle, dates]) => {
        Object.entries(dates).forEach(([date, info]) => {
          const pushRow = (f) => {
            const km = kmFromFill(f);
            loaded.push(
              calcRow({
                date,
                time: f.t || "",
                vehicle,
                owner: f.owner || "",
                driver: f.d || "",
                litres: f.l || "",
                amount: f.a || "",
                payType: f.payType || "",
                paid: f.p || "",
                station: f.station || "",
                openKm: km.openKm,
                closeKm: km.closeKm,
                totalKm: km.totalKm,
                expKmL: "",
                actualKmL: "",
                expKm: "",
                actualKm: "",
                kmDiff: "",
              })
            );
          };
          if (info.fills) info.fills.forEach(pushRow);
          else pushRow(info);
        });
      });
      loaded.sort((a,b) => {
        const [da,ma] = (a.date||'').split('-');
        const [db,mb] = (b.date||'').split('-');
        return (MONTHS.indexOf(ma)-MONTHS.indexOf(mb)) || (parseInt(da)-parseInt(db));
      });
      for (let i = 0; i < 20; i++) loaded.push(emptyRow());
      setRows(loaded);
    } catch {
      setRows(Array.from({length:20}, emptyRow));
    }
  };

  const pushHistory = useCallback((currentRows) => {
    setHistory(h => { const n = [...h, JSON.stringify(currentRows)]; return n.slice(-50); });
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setHistory(h => {
      if (!h.length) return h;
      const prev = JSON.parse(h[h.length-1]);
      setFuture(f => [JSON.stringify(rows), ...f]);
      setRows(prev);
      return h.slice(0,-1);
    });
  }, [rows]);

  const redo = useCallback(() => {
    setFuture(f => {
      if (!f.length) return f;
      const next = JSON.parse(f[0]);
      setHistory(h => [...h, JSON.stringify(rows)]);
      setRows(next);
      return f.slice(1);
    });
  }, [rows]);

  const updateCell = useCallback((r, field, val, noHistory=false) => {
    setRows(prev => {
      if (!noHistory) pushHistory(prev);
      const next = prev.map((row, i) => {
        if (i !== r) return row;
        let v = MD_UPPERCASE.includes(field) ? val.toUpperCase() : val;
        const updated = { ...row, [field]: v };
        return calcRow(updated);
      });
      const last = next[next.length-1];
      if (val && r === next.length-1 && MD_COLS.some(k => last[k])) {
        next.push(emptyRow());
      }
      return next;
    });
  }, [pushHistory]);

  const normSel = (s) => ({
    r1: Math.min(s.r1, s.r2), r2: Math.max(s.r1, s.r2),
    c1: Math.min(s.c1, s.c2), c2: Math.max(s.c1, s.c2),
  });
  const isSelected = (r,c) => {
    const s = normSel(sel);
    return r>=s.r1 && r<=s.r2 && c>=s.c1 && c<=s.c2;
  };

  const selectCell = (r, c, extend=false) => {
    setSel(s => ({ r1: extend?s.r1:r, c1: extend?s.c1:c, r2:r, c2:c }));
    setTimeout(() => {
      const inp = tableRef.current?.querySelector(`td[data-r="${r}"][data-c="${c}"] input, td[data-r="${r}"][data-c="${c}"] select`);
      if (inp) inp.focus({ preventScroll: true });
    }, 0);
  };
  const selectRow = (r) => setSel({ r1:r, c1:0, r2:r, c2:MD_COLS.length-1 });

  const doCopy = () => {
    const s = normSel(sel);
    const lines = [];
    for (let r=s.r1; r<=s.r2; r++) {
      const cells = [];
      for (let c=s.c1; c<=s.c2; c++) cells.push(rows[r]?.[MD_COLS[c]] ?? '');
      lines.push(cells.join('\t'));
    }
    navigator.clipboard.writeText(lines.join('\n')).catch(()=>{});
  };

  const doCut = () => {
    doCopy();
    pushHistory(rows);
    const s = normSel(sel);
    setRows(prev => {
      const next = prev.map((row,r) => {
        if (r<s.r1||r>s.r2) return row;
        const updated = {...row};
        for (let c=s.c1; c<=s.c2; c++) updated[MD_COLS[c]] = '';
        return calcRow(updated);
      });
      return next;
    });
  };

  const pasteAt = (text, startR, startC) => {
    pushHistory(rows);
    const pasteRows = text.trim().split(/\r?\n/);
    setRows(prev => {
      const next = [...prev];
      pasteRows.forEach((line, ri) => {
        const cols = line.split('\t');
        while (next.length <= startR+ri) next.push(emptyRow());
        cols.forEach((val, ci) => {
          const colIdx = startC+ci;
          if (colIdx >= MD_COLS.length) return;
          const field = MD_COLS[colIdx];
          let v = val.trim();
          if (MD_UPPERCASE.includes(field)) v = v.toUpperCase();
          next[startR+ri] = { ...next[startR+ri], [field]: v };
        });
        next[startR+ri] = calcRow(next[startR+ri]);
      });
      return next;
    });
  };

  const deleteSel = () => {
    pushHistory(rows);
    const s = normSel(sel);
    setRows(prev => prev.map((row,r) => {
      if (r<s.r1||r>s.r2) return row;
      const updated = {...row};
      for (let c=s.c1; c<=s.c2; c++) updated[MD_COLS[c]] = '';
      return calcRow(updated);
    }));
  };

  const handleKeyNav = (e, r, c) => {
    const key = e.key;
    if (e.ctrlKey || e.metaKey) {
      if (key==='c')               { doCopy(); return; }
      if (key==='x')               { doCut();  return; }
      if (key==='z') { e.preventDefault(); undo(); return; }
      if (key==='y') { e.preventDefault(); redo(); return; }
      if (key==='a') { e.preventDefault(); setSel({r1:0,c1:0,r2:rows.length-1,c2:MD_COLS.length-1}); return; }
    }
    let nr=r, nc=c;
    if      (key==='Tab')        { e.preventDefault(); nc = c+(e.shiftKey?-1:1); }
    else if (key==='Enter')      { e.preventDefault(); nr = r+(e.shiftKey?-1:1); }
    else if (key==='ArrowRight') { e.preventDefault(); nc = c+1; }
    else if (key==='ArrowLeft')  { e.preventDefault(); nc = c-1; }
    else if (key==='ArrowDown')  { e.preventDefault(); nr = r+1; }
    else if (key==='ArrowUp')    { e.preventDefault(); nr = r-1; }
    else if (key==='Delete')     { e.preventDefault(); deleteSel(); return; }
    else return;

    nc = Math.max(0, Math.min(nc, MD_COLS.length-1));
    nr = Math.max(0, Math.min(nr, rows.length-1));
    selectCell(nr, nc, e.shiftKey);
  };

  const doSort = (c) => {
    pushHistory(rows);
    const field = MD_COLS[c];
    const asc   = sortCol===c ? !sortAsc : true;
    setSortCol(c); setSortAsc(asc);
    setRows(prev => {
      const empty = prev.filter(r => !r[field]);
      const full  = prev.filter(r => !!r[field]);
      full.sort((a,b) => {
        const va=a[field]||'', vb=b[field]||'';
        const na=parseFloat(va), nb=parseFloat(vb);
        const cmp = (!isNaN(na)&&!isNaN(nb)) ? na-nb : va.localeCompare(vb);
        return asc ? cmp : -cmp;
      });
      return [...full, ...empty];
    });
  };

  const addRow = () => {
    setRows(prev => {
      const next = [...prev, emptyRow()];
      setTimeout(() => {
        const wrap = document.getElementById('md-scroll-wrap');
        if (wrap) wrap.scrollTop = wrap.scrollHeight;
      }, 50);
      return next;
    });
  };

  const delRow = (i) => {
    pushHistory(rows);
    setRows(prev => prev.filter((_,idx) => idx!==i));
  };

  const saveAll = async () => {
    const valid = rows.filter(r => r.vehicle && r.date && r.litres);
    if (!valid.length) {
      setSaveMsg(<span style={{color:'#c00'}}>No valid rows to save</span>);
      setTimeout(() => setSaveMsg(''), 3000);
      return;
    }
    const monthKey = `${selMonth}-${selYear}`;
    const data = {};
    valid.forEach(r => {
      const v = r.vehicle.trim().toUpperCase();
      const d = r.date.trim();
      if (!v || !d) return;
      if (!data[v]) data[v] = {};
      const cr = calcRow(r);
      const entry = {
        l: parseFloat(r.litres) || 0,
        a: parseInt(r.amount, 10) || 0,
        d: r.driver || "",
        p: r.paid || "",
        t: r.time || "",
        owner: r.owner || "",
        payType: r.payType || "",
        station: r.station || "",
      };
      const ok = parseFloat(r.openKm);
      const ck = parseFloat(r.closeKm);
      const tk = parseFloat(cr.totalKm);
      if (!Number.isNaN(ok) && ok > 0) entry.openKm = ok;
      if (!Number.isNaN(ck) && ck > 0) entry.closeKm = ck;
      if (!Number.isNaN(tk) && tk > 0) entry.runKm = tk;
      if (!data[v][d]) {
        data[v][d] = entry;
      } else {
        if (!data[v][d].fills) data[v][d].fills = [{...data[v][d]}];
        data[v][d].fills.push(entry);
        data[v][d].l += entry.l;
        data[v][d].a += entry.a;
      }
    });
    try {
      await axios.post(`${API_BASE_URL}/api/fills/bulk`, { month: monthKey, data });
      setSaveMsg(<span style={{color:'#166534'}}>{valid.length} rows saved</span>);
    } catch {
      setSaveMsg(<span style={{color:'#c00'}}>Save failed</span>);
    }
    setTimeout(() => setSaveMsg(''), 3000);
  };

  const getDlRows = () => {
    return rows.filter(r => {
      if (!r.date && !r.vehicle) return false;
      if (dlMonth && !r.date.includes(`-${dlMonth}`)) return false;
      if (dlDay   && !r.date.startsWith(dlDay+'-'))   return false;
      if (dlVehicle && !(r.vehicle||'').toLowerCase().includes(dlVehicle.toLowerCase())) return false;
      return true;
    });
  };

  const toggleDlCol = (key) => {
    setDlCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const dlSelectAll = (val) => setDlCols(val ? new Set(DL_COLS.map(c=>c.key)) : new Set());

  const download = (format) => {
    const dlRows = getDlRows();
    if (!dlRows.length) { alert('No data matches the selected filters.'); return; }
    const cols   = DL_COLS.filter(c => dlCols.has(c.key));
    const header = cols.map(c => c.label);
    const data   = dlRows.map(r => cols.map(c => r[c.key] ?? ''));

    if (format==='csv') {
      const lines = [header,...data].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(','));
      const blob  = new Blob([lines.join('\n')], {type:'text/csv'});
      const a     = document.createElement('a');
      a.href      = URL.createObjectURL(blob);
      a.download  = `sendo_data_${dlMonth||'All'}_${dlYear||selYear}.csv`;
      a.click();
    } else {
      const loadXLSX = () => {
        const ws = window.XLSX.utils.aoa_to_sheet([header,...data]);
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, 'Sendo Data');
        window.XLSX.writeFile(wb, `sendo_data_${dlMonth||'All'}_${dlYear||selYear}.xlsx`);
      };
      if (typeof window.XLSX === 'undefined') {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        s.onload = loadXLSX;
        document.head.appendChild(s);
      } else {
        loadXLSX();
      }
    }
  };

  // ── Design tokens matching screenshot ────────────────────────────────────
  const css = {
    page: {
      marginLeft: typeof window !== 'undefined' && window.innerWidth <= 768 ? 0 : 260,
      marginRight: 0,
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#ffffff',
      color: '#000000',
      minHeight: 'calc(100vh - 70px)',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
    heading: {
      backgroundColor: '#FFC107',
      color: '#000000',
      padding: '14px 24px',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      margin: 0,
    },
    body: { padding: '20px 24px' },

    // Top bar
    topBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 20,
    },
    topTitle: {
      fontSize: '1rem',
      fontWeight: 'bold',
      color: '#000000',
    },
    topSub: {
      fontSize: '0.78rem',
      color: '#555555',
      marginTop: 3,
    },
    select: {
      background: '#ffffff',
      border: '1.5px solid #000000',
      borderRadius: 3,
      color: '#000000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '0.82rem',
      padding: '7px 10px',
      outline: 'none',
      cursor: 'pointer',
      height: 36,
    },
    btnSave: {
      background: '#FFC107',
      color: '#000000',
      border: '1.5px solid #FFC107',
      borderRadius: 3,
      padding: '7px 20px',
      fontWeight: 'bold',
      fontSize: '0.82rem',
      cursor: 'pointer',
      height: 36,
    },
    btnAdd: {
      background: '#ffffff',
      border: '1.5px solid #000000',
      color: '#000000',
      borderRadius: 3,
      padding: '7px 16px',
      fontSize: '0.82rem',
      cursor: 'pointer',
      height: 36,
    },

    // Section title style (matching screenshot)
    sectionTitle: {
      fontSize: '0.95rem',
      fontWeight: 'bold',
      color: '#000000',
      marginBottom: 0,
    },
    sectionDivider: {
      border: 'none',
      borderBottom: '2px solid #FFC107',
      margin: '8px 0 16px',
    },

    // Download / filter bar
    dlBar: {
      background: '#ffffff',
      border: '1.5px solid #000000',
      borderRadius: 4,
      padding: '16px 18px',
      marginBottom: 16,
    },
    dlBarTop: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
      marginBottom: 12,
    },
    dlLbl: {
      fontSize: '0.7rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      color: '#000000',
      marginBottom: 4,
      letterSpacing: '0.06em',
    },
    dlSelect: {
      background: '#ffffff',
      border: '1.5px solid #000000',
      borderRadius: 3,
      color: '#000000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '0.8rem',
      padding: '6px 9px',
      outline: 'none',
      cursor: 'pointer',
      height: 34,
    },
    dlInput: {
      background: '#ffffff',
      border: '1.5px solid #000000',
      borderRadius: 3,
      color: '#000000',
      fontFamily: 'Arial, sans-serif',
      fontSize: '0.8rem',
      padding: '6px 9px',
      outline: 'none',
      width: 150,
      height: 34,
    },
    dlDivider: {
      width: 1,
      height: 34,
      background: '#cccccc',
      flexShrink: 0,
      alignSelf: 'flex-end',
    },
    btnCsv: {
      background: '#ffffff',
      color: '#000000',
      border: '1.5px solid #000000',
      borderRadius: 3,
      padding: '6px 16px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      fontWeight: 'bold',
      height: 34,
    },
    btnXl: {
      background: '#FFC107',
      color: '#000000',
      border: '1.5px solid #FFC107',
      borderRadius: 3,
      padding: '6px 16px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      fontWeight: 'bold',
      height: 34,
    },
    dlChipsWrap: {
      borderTop: '1px solid #e0e0e0',
      paddingTop: 12,
    },
    dlChipsLbl: {
      fontSize: '0.7rem',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      color: '#000000',
      letterSpacing: '0.06em',
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    dlChips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 6,
    },
    dlFoot: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 10,
      paddingTop: 10,
      borderTop: '1px solid #e0e0e0',
    },
    dlCount: {
      fontSize: '0.78rem',
      color: '#555555',
    },

    // Spreadsheet wrapper
    sheetWrap: {
      background: '#ffffff',
      border: '1.5px solid #000000',
      borderRadius: 4,
      overflow: 'hidden',
    },
    kbHint: {
      display: 'flex',
      gap: 14,
      padding: '7px 14px',
      background: '#f5f5f5',
      borderBottom: '1px solid #cccccc',
      fontSize: '0.7rem',
      color: '#555555',
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    sheetScroll: {
      overflowX: 'auto',
      maxHeight: '62vh',
      overflowY: 'auto',
    },
    sheetFoot: {
      padding: '8px 14px',
      borderTop: '1px solid #cccccc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: '#f5f5f5',
    },
  };

  const thStyle = () => ({
    padding: '9px 10px',
    border: '1px solid #cccccc',
    textAlign: 'left',
    fontSize: '0.68rem',
    fontWeight: 'bold',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#000000',
    whiteSpace: 'nowrap',
    background: '#f5f5f5',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  });

  const dlChipStyle = (on) => ({
    padding: '4px 12px',
    borderRadius: 3,
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    userSelect: 'none',
    border: on ? '1.5px solid #FFC107' : '1.5px solid #cccccc',
    background: on ? '#FFF8E1' : '#ffffff',
    color: '#000000',
  });

  const cellStyle = (r, c, bg) => ({
    padding: 0,
    border: isSelected(r,c) ? '2px solid #FFC107' : '1px solid #e0e0e0',
    background: isSelected(r,c) ? '#FFFDE7' : (bg || '#ffffff'),
    cursor: 'text',
    outline: 'none',
  });

  const inputStyle = (align, readOnly) => ({
    width: '100%',
    padding: '5px 7px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#000000',
    fontFamily: 'Arial, sans-serif',
    fontSize: '0.78rem',
    textAlign: align,
    boxSizing: 'border-box',
    cursor: readOnly ? 'default' : 'text',
  });

  const inputReadOnlyStyle = (align) => ({
    width: '100%',
    padding: '5px 7px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#555555',
    fontFamily: 'Arial, sans-serif',
    fontSize: '0.78rem',
    textAlign: align,
    boxSizing: 'border-box',
    cursor: 'default',
  });

  const kmDiffColor = (val) => {
    if (val === '') return '#888888';
    return val >= 0 ? '#166534' : '#c00000';
  };

  return (
    <div style={css.page}>
      {/* Header */}
      <h2 style={css.heading}>Monthly Data Entry</h2>

      <div style={css.body}>

        {/* Top Bar */}
        <div style={css.topBar}>
          <div>
            <div style={css.topTitle}>Monthly Data Entry</div>
            <div style={css.topSub}>Type or paste data directly into the spreadsheet below</div>
          </div>
          <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
            <select style={css.select} value={selMonth} onChange={e=>setSelMonth(e.target.value)}>
              {MONTHS.map(m=><option key={m}>{m}</option>)}
            </select>
            <select style={css.select} value={selYear} onChange={e=>setSelYear(e.target.value)}>
              {YEARS.map(y=><option key={y}>{y}</option>)}
            </select>
            <button style={css.btnSave} onClick={saveAll}>Save All</button>
            <button style={css.btnAdd}  onClick={addRow}>+ Add Row</button>
            <span style={{fontSize:'0.78rem', minWidth:100}}>{saveMsg}</span>
          </div>
        </div>

        {/* Filter and Download Bar */}
        <div style={css.dlBar}>
          <div style={css.dlBarTop}>
            <div>
              <div style={css.dlLbl}>Month</div>
              <select style={css.dlSelect} value={dlMonth} onChange={e=>setDlMonth(e.target.value)}>
                <option value="">All</option>
                {MONTHS.map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <div style={css.dlLbl}>Year</div>
              <select style={css.dlSelect} value={dlYear} onChange={e=>setDlYear(e.target.value)}>
                <option value="">All</option>
                {YEARS.map(y=><option key={y}>{y}</option>)}
              </select>
            </div>
            <div style={css.dlDivider}/>
            <div>
              <div style={css.dlLbl}>Day</div>
              <select style={css.dlSelect} value={dlDay} onChange={e=>setDlDay(e.target.value)}>
                <option value="">All Days</option>
                {DAYS.map(d=><option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <div style={css.dlLbl}>Vehicle</div>
              <input
                style={css.dlInput}
                placeholder="e.g. KA01AE8482"
                value={dlVehicle}
                onChange={e=>setDlVehicle(e.target.value)}
              />
            </div>
            <div style={css.dlDivider}/>
            <div style={{display:'flex', gap:8, marginLeft:'auto', alignItems:'flex-end'}}>
              <button style={css.btnCsv} onClick={()=>download('csv')}>Download CSV</button>
              <button style={css.btnXl}  onClick={()=>download('excel')}>Download Excel</button>
            </div>
          </div>

          {/* Column chips */}
          <div style={css.dlChipsWrap}>
            <div style={css.dlChipsLbl}>
              <span>Columns to include</span>
              <span style={{display:'flex', gap:10}}>
                <span
                  onClick={()=>dlSelectAll(true)}
                  style={{cursor:'pointer', color:'#000000', fontSize:'0.68rem', fontWeight:'bold', textDecoration:'underline'}}
                >
                  Select All
                </span>
                <span style={{color:'#cccccc'}}>|</span>
                <span
                  onClick={()=>dlSelectAll(false)}
                  style={{cursor:'pointer', color:'#555555', fontSize:'0.68rem', textDecoration:'underline'}}
                >
                  Clear
                </span>
              </span>
            </div>
            <div style={css.dlChips}>
              {DL_COLS.map(col=>(
                <span
                  key={col.key}
                  style={dlChipStyle(dlCols.has(col.key))}
                  onClick={()=>toggleDlCol(col.key)}
                >
                  {col.label}
                </span>
              ))}
            </div>
          </div>

          <div style={css.dlFoot}>
            <div style={css.dlCount}>
              <b>{dlCount} rows</b> match your filters &nbsp;·&nbsp; <b>{dlCols.size}</b> columns selected
            </div>
          </div>
        </div>

        {/* Spreadsheet */}
        <div style={css.sheetWrap}>
          {/* Keyboard hint */}
          <div style={css.kbHint}>
            <span><b>Ctrl+C</b> Copy</span>
            <span><b>Ctrl+V</b> Paste</span>
            <span><b>Ctrl+Z</b> Undo</span>
            <span><b>Ctrl+Y</b> Redo</span>
            <span><b>Del</b> Clear</span>
            <span><b>Tab / Enter</b> Navigate</span>
            <span><b>Arrow keys</b> Move</span>
            <span><b>Shift+Click</b> Extend selection</span>
            <span style={{marginLeft:'auto', fontSize:'0.7rem', color:'#000000'}}>
              {sel.r1>=0 ? `${Math.abs(sel.r2-sel.r1)+1} x ${Math.abs(sel.c2-sel.c1)+1} selected` : ''}
            </span>
          </div>

          {/* Table */}
          <div id="md-scroll-wrap" style={css.sheetScroll}>
            <table
              ref={tableRef}
              style={{width:'100%', borderCollapse:'collapse', fontSize:'0.78rem', minWidth:1600}}
            >
              <thead>
                <tr>
                  <th style={{...thStyle(), width:36, textAlign:'center'}}>#</th>
                  {COL_HEADERS.map((h,c)=>(
                    <th
                      key={c}
                      style={{...thStyle(), cursor: h.sortable?'pointer':'default'}}
                      onClick={h.sortable ? ()=>doSort(c) : undefined}
                      title={h.sortable ? 'Click to sort' : undefined}
                    >
                      {h.label}{h.sortable && (sortCol===c ? (sortAsc?' asc':' desc') : '')}
                    </th>
                  ))}
                  <th style={{...thStyle(), width:36, textAlign:'center'}}>Del</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row,r)=>(
                  <tr key={r}>
                    {/* Row number */}
                    <td
                      style={{
                        padding:'4px 6px', border:'1px solid #e0e0e0',
                        textAlign:'center', fontSize:'0.68rem',
                        color:'#888888', background:'#f9f9f9',
                        cursor:'pointer', userSelect:'none'
                      }}
                      onClick={()=>selectRow(r)}
                    >
                      {r+1}
                    </td>

                    {/* Date */}
                    <td data-r={r} data-c={0} style={cellStyle(r,0,COL_BG[0])}
                      onMouseDown={()=>{setSel({r1:r,c1:0,r2:r,c2:0}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:0})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} value={row.date} placeholder="01-Jan"
                        onChange={e=>updateCell(r,'date',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,0)}
                        onFocus={()=>selectCell(r,0)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,0); e.preventDefault();}}/>
                    </td>

                    {/* Time */}
                    <td data-r={r} data-c={1} style={cellStyle(r,1,COL_BG[1])}
                      onMouseDown={()=>{setSel({r1:r,c1:1,r2:r,c2:1}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:1})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} value={row.time} placeholder="8:00 AM"
                        onChange={e=>updateCell(r,'time',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,1)}
                        onFocus={()=>selectCell(r,1)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,1); e.preventDefault();}}/>
                    </td>

                    {/* Vehicle */}
                    <td data-r={r} data-c={2} style={cellStyle(r,2,COL_BG[2])}
                      onMouseDown={()=>{setSel({r1:r,c1:2,r2:r,c2:2}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:2})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={{...inputStyle('left'), fontWeight:'bold'}} value={row.vehicle} placeholder="KA01AE8482"
                        onChange={e=>updateCell(r,'vehicle',e.target.value.toUpperCase())}
                        onKeyDown={e=>handleKeyNav(e,r,2)}
                        onFocus={()=>selectCell(r,2)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,2); e.preventDefault();}}/>
                    </td>

                    {/* Owner */}
                    <td data-r={r} data-c={3} style={cellStyle(r,3,COL_BG[3])}
                      onMouseDown={()=>{setSel({r1:r,c1:3,r2:r,c2:3}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:3})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('left')} value={row.owner} placeholder="OWNER NAME"
                        onChange={e=>updateCell(r,'owner',e.target.value.toUpperCase())}
                        onKeyDown={e=>handleKeyNav(e,r,3)}
                        onFocus={()=>selectCell(r,3)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,3); e.preventDefault();}}/>
                    </td>

                    {/* Driver */}
                    <td data-r={r} data-c={4} style={cellStyle(r,4,COL_BG[4])}
                      onMouseDown={()=>{setSel({r1:r,c1:4,r2:r,c2:4}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:4})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('left')} value={row.driver} placeholder="DRIVER NAME"
                        onChange={e=>updateCell(r,'driver',e.target.value.toUpperCase())}
                        onKeyDown={e=>handleKeyNav(e,r,4)}
                        onFocus={()=>selectCell(r,4)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,4); e.preventDefault();}}/>
                    </td>

                    {/* Litres */}
                    <td data-r={r} data-c={5} style={cellStyle(r,5,COL_BG[5])}
                      onMouseDown={()=>{setSel({r1:r,c1:5,r2:r,c2:5}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:5})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} type="number" value={row.litres} placeholder="0.0"
                        onChange={e=>updateCell(r,'litres',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,5)}
                        onFocus={()=>selectCell(r,5)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,5); e.preventDefault();}}/>
                    </td>

                    {/* Amount */}
                    <td data-r={r} data-c={6} style={cellStyle(r,6,COL_BG[6])}
                      onMouseDown={()=>{setSel({r1:r,c1:6,r2:r,c2:6}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:6})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} type="number" value={row.amount} placeholder="0"
                        onChange={e=>updateCell(r,'amount',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,6)}
                        onFocus={()=>selectCell(r,6)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,6); e.preventDefault();}}/>
                    </td>

                    {/* Payment Type */}
                    <td data-r={r} data-c={7} style={cellStyle(r,7,COL_BG[7])}
                      onMouseDown={()=>{setSel({r1:r,c1:7,r2:r,c2:7}); setDragging(true);}}
                      onMouseUp={()=>setDragging(false)}>
                      <select
                        style={{
                          width:'100%', padding:'5px 6px',
                          background:'transparent', border:'none', outline:'none',
                          color:'#000000', fontSize:'0.77rem', fontFamily:'Arial, sans-serif',
                        }}
                        value={row.payType}
                        onChange={e=>updateCell(r,'payType',e.target.value)}
                        onFocus={()=>selectCell(r,7)}
                      >
                        <option value="">Select</option>
                        {PAY_TYPES.map(p=><option key={p}>{p}</option>)}
                      </select>
                    </td>

                    {/* Paid By */}
                    <td data-r={r} data-c={8} style={cellStyle(r,8,COL_BG[8])}
                      onMouseDown={()=>{setSel({r1:r,c1:8,r2:r,c2:8}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:8})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('left')} value={row.paid} placeholder="PAID BY"
                        onChange={e=>updateCell(r,'paid',e.target.value.toUpperCase())}
                        onKeyDown={e=>handleKeyNav(e,r,8)}
                        onFocus={()=>selectCell(r,8)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,8); e.preventDefault();}}/>
                    </td>

                    {/* Fuel Station */}
                    <td data-r={r} data-c={9} style={cellStyle(r,9,COL_BG[9])}
                      onMouseDown={()=>{setSel({r1:r,c1:9,r2:r,c2:9}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:9})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('left')} value={row.station} placeholder="STATION NAME"
                        onChange={e=>updateCell(r,'station',e.target.value.toUpperCase())}
                        onKeyDown={e=>handleKeyNav(e,r,9)}
                        onFocus={()=>selectCell(r,9)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,9); e.preventDefault();}}/>
                    </td>

                    {/* Opening KM */}
                    <td data-r={r} data-c={10} style={cellStyle(r,10,COL_BG[10])}
                      onMouseDown={()=>{setSel({r1:r,c1:10,r2:r,c2:10}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:10})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} type="number" value={row.openKm} placeholder=""
                        onChange={e=>updateCell(r,'openKm',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,10)}
                        onFocus={()=>selectCell(r,10)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,10); e.preventDefault();}}/>
                    </td>

                    {/* Closing KM */}
                    <td data-r={r} data-c={11} style={cellStyle(r,11,COL_BG[11])}
                      onMouseDown={()=>{setSel({r1:r,c1:11,r2:r,c2:11}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:11})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} type="number" value={row.closeKm} placeholder=""
                        onChange={e=>updateCell(r,'closeKm',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,11)}
                        onFocus={()=>selectCell(r,11)}
                        onPaste={e=>{pasteAt((e.clipboardData||window.clipboardData).getData('text'),r,11); e.preventDefault();}}/>
                    </td>

                    {/* Total KM - readonly */}
                    <td data-r={r} data-c={12} style={cellStyle(r,12,COL_BG[12])}>
                      <input style={inputReadOnlyStyle('center')} value={row.totalKm||''} readOnly tabIndex={-1}/>
                    </td>

                    {/* Exp KM/L - editable */}
                    <td data-r={r} data-c={13} style={cellStyle(r,13,COL_BG[13])}
                      onMouseDown={()=>{setSel({r1:r,c1:13,r2:r,c2:13}); setDragging(true);}}
                      onMouseOver={()=>{ if(dragging) setSel(s=>({...s,r2:r,c2:13})); }}
                      onMouseUp={()=>setDragging(false)}>
                      <input style={inputStyle('center')} type="number" value={row.expKmL} placeholder=""
                        onChange={e=>updateCell(r,'expKmL',e.target.value)}
                        onKeyDown={e=>handleKeyNav(e,r,13)}
                        onFocus={()=>selectCell(r,13)}/>
                    </td>

                    {/* Actual KM/L - readonly */}
                    <td data-r={r} data-c={14} style={cellStyle(r,14,COL_BG[14])}>
                      <input style={inputReadOnlyStyle('center')} value={row.actualKmL||''} readOnly tabIndex={-1}/>
                    </td>

                    {/* Exp KM - readonly */}
                    <td data-r={r} data-c={15} style={cellStyle(r,15,COL_BG[15])}>
                      <input style={inputReadOnlyStyle('center')} value={row.expKm||''} readOnly tabIndex={-1}/>
                    </td>

                    {/* Actual KM - readonly */}
                    <td data-r={r} data-c={16} style={cellStyle(r,16,COL_BG[16])}>
                      <input style={inputReadOnlyStyle('center')} value={row.actualKm||''} readOnly tabIndex={-1}/>
                    </td>

                    {/* KM Difference - readonly, color coded */}
                    <td data-r={r} data-c={17} style={cellStyle(r,17,COL_BG[17])}>
                      <input
                        style={{...inputReadOnlyStyle('center'), color: kmDiffColor(row.kmDiff), fontWeight: row.kmDiff !== '' ? 'bold' : 'normal'}}
                        value={row.kmDiff !== '' ? (row.kmDiff >= 0 ? '+' : '') + row.kmDiff : ''}
                        readOnly
                        tabIndex={-1}
                      />
                    </td>

                    {/* Delete button */}
                    <td style={{padding:0, border:'1px solid #e0e0e0', textAlign:'center'}}>
                      <button
                        onClick={()=>delRow(r)}
                        style={{
                          background:'none', border:'none',
                          color:'#cccccc', cursor:'pointer',
                          fontSize:'0.82rem', padding:'4px 8px',
                        }}
                        onMouseEnter={e=>e.target.style.color='#c00000'}
                        onMouseLeave={e=>e.target.style.color='#cccccc'}
                      >
                        x
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sheet footer */}
          <div style={css.sheetFoot}>
            <span style={{fontSize:'0.72rem', color:'#555555'}}>{rows.length} rows</span>
            <span style={{fontSize:'0.7rem', color:'#555555'}}>
              Press Tab to move to next cell &nbsp;|&nbsp; Use Ctrl+V to paste data
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}